import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Save as SaveIcon, DarkMode, LightMode } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useThemeContext } from '../context/ThemeContext';
import Layout from '../components/Layout/Layout';
import { getTimezones } from '../utils/dateUtils';

const SettingsPage = () => {
  const { user, updateUserSettings } = useAuth();
  const { mode, toggleTheme } = useThemeContext();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  
  const [settings, setSettings] = useState({
    timezone: user?.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    emailNotifications: user?.settings?.emailNotifications ?? true,
    pushNotifications: user?.settings?.pushNotifications ?? true,
    dateFormat: user?.settings?.dateFormat || 'DD/MM/YYYY',
    timeFormat: user?.settings?.timeFormat || '24h',
  });

  const timezones = getTimezones();

  const handleSettingsUpdate = async () => {
  setLoading(true);
  try {
    // Преобразуем формат даты для отправки на сервер
    const settingsToSend = { ...settings };
    
    // Если формат даты старый, отправляем его как есть (сервер преобразует)
    // Если новый, тоже отправляем
    await updateUserSettings(settingsToSend);
    setSnackbar({ open: true, message: 'Settings saved successfully', severity: 'success' });
  } catch (error) {
    setSnackbar({ open: true, message: error.response?.data?.message || 'Save failed', severity: 'error' });
  } finally {
    setLoading(false);
  }
};

  return (
    <Layout>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Preferences
        </Typography>
        
        <Grid container spacing={3}>
          {/* Appearance */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Appearance</Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    {mode === 'light' ? <LightMode /> : <DarkMode />}
                    <Typography>
                      {mode === 'light' ? 'Light Mode' : 'Dark Mode'}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={toggleTheme}
                    startIcon={mode === 'light' ? <DarkMode /> : <LightMode />}
                  >
                    Switch to {mode === 'light' ? 'Dark' : 'Light'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Date & Time */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Date & Time</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={settings.timezone}
                        onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                        label="Timezone"
                      >
                        {timezones.map(tz => (
                          <MenuItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Date Format</InputLabel>
                      <Select
                        value={settings.dateFormat}
                        onChange={(e) => setSettings({ ...settings, dateFormat: e.target.value })}
                        label="Date Format"
                      >
                        <MenuItem value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</MenuItem>
                        <MenuItem value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</MenuItem>
                        <MenuItem value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Time Format</InputLabel>
                      <Select
                        value={settings.timeFormat}
                        onChange={(e) => setSettings({ ...settings, timeFormat: e.target.value })}
                        label="Time Format"
                      >
                        <MenuItem value="24h">24-hour (14:30)</MenuItem>
                        <MenuItem value="12h">12-hour (02:30 PM)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Notifications */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Notifications</Typography>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.emailNotifications}
                        onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                      />
                    }
                    label="Email Notifications"
                  />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.pushNotifications}
                        onChange={(e) => setSettings({ ...settings, pushNotifications: e.target.checked })}
                      />
                    }
                    label="Push Notifications"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Save Button */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSettingsUpdate}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save All Settings'}
              </Button>
            </Box>
          </Grid>
        </Grid>
        
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default SettingsPage;